/* eslint-disable max-lines */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { X } from 'lucide-react';

import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';

import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useGetUsers } from '@utils/api/users/use-get-users.tsx';
import { useGetTeam } from '@utils/api/teams/use-get-team.tsx';
import { useGetUsersOfTeam } from '@utils/api/teams/use-get-team-users.tsx';
import { useAddMembersToTeam } from '@utils/api/teams/use-add-to-team.tsx';
import { useDeleteUserFromTeam } from '@utils/api/teams/use-remove-user-from-team.tsx';
import { useUpdateOrganizationGroup } from '@utils/api/teams/use-update-team.tsx';
import { BaseError } from '@utils/types/shared/base.ts';

interface Props {
  teamId: number;
  editTeam: boolean;
  setEditTeam: React.Dispatch<React.SetStateAction<boolean>>;
}

type ValidationErr = { loc?: string[]; msg?: string };

function parseError(err: unknown, fallback: string): string {
  const data = (err as AxiosError<BaseError>)?.response?.data;
  if (Array.isArray(data?.detail)) {
    return (data.detail as ValidationErr[])
      .map(e => `${e.loc?.[1] ?? 'field'}: ${e.msg}`)
      .join('\n');
  }
  if (data?.message) return data.message;
  if (data?.error_description) return data.error_description;
  return fallback;
}

function getInitials(first?: string, last?: string): string {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f[0].toUpperCase();
  if (l) return l[0].toUpperCase();
  return '?';
}

const FORM_ID = 'teams-update-form';
const MAX_PREVIEW_AVATARS = 5;

const TeamsUpdateForm: React.FC<Props> = ({
  teamId,
  editTeam,
  setEditTeam
}) => {
  const { t } = useTranslation();
  const { getColour, getBrandTint, theme } = useThemeContext();
  const isDark = theme === 'dark';

  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [selectedUser, setSelectedUser] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: teamData, isLoading: isTeamLoading } = useGetTeam(
    teamId.toString()
  );
  const { data: teamUsersData, refetch: refetchTeamUsers } = useGetUsersOfTeam(
    teamId.toString()
  );

  const {
    data: users,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching
  } = useGetUsers();

  const { mutateAsync: addMembersToTeam } = useAddMembersToTeam();
  const { mutateAsync: deleteUserFromTeam } = useDeleteUserFromTeam();
  const { mutateAsync: updateOrganizationGroup } = useUpdateOrganizationGroup();

  useEffect(() => {
    if (editTeam && teamData) {
      setTeamName(teamData.name ?? '');
      setTeamDescription(teamData.description ?? '');
    }
  }, [editTeam, teamData]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setEditTeam(false);
      setSelectedUser(null);
    }
  }, [isSubmitting, setEditTeam]);

  const handleSaveChanges = useCallback(async () => {
    setIsSubmitting(true);
    const toastId = toast.loading(t('updating-team'));

    try {
      await updateOrganizationGroup({
        teamId,
        payload: {
          name: teamName || teamData?.name,
          description: teamDescription || teamData?.description
        }
      });
      toast.success(t('team-was-updated-successfully'), { id: toastId });
      setEditTeam(false);
    } catch (err: unknown) {
      toast.error(parseError(err, t('failed-to-update-team')), { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    teamId,
    teamName,
    teamDescription,
    teamData,
    updateOrganizationGroup,
    setEditTeam,
    t
  ]);

  const handleAddUser = useCallback(async () => {
    if (!selectedUser) return;

    const toastId = toast.loading(t('adding-member'));
    try {
      await addMembersToTeam({ teamId, userIds: [selectedUser.value] });
      toast.success(t('member-added-successfully'), { id: toastId });
      setSelectedUser(null);
      refetchTeamUsers();
    } catch (err: unknown) {
      toast.error(parseError(err, t('error-adding-member')), { id: toastId });
    }
  }, [selectedUser, teamId, addMembersToTeam, refetchTeamUsers, t]);

  const handleRemoveUser = useCallback(
    async (userId: number) => {
      const toastId = toast.loading(t('removing-member'));
      try {
        await deleteUserFromTeam({ teamId, userId });
        toast.success(t('member-removed-successfully'), { id: toastId });
        refetchTeamUsers();
      } catch (err: unknown) {
        toast.error(parseError(err, t('error-removing-member')), {
          id: toastId
        });
      }
    },
    [teamId, deleteUserFromTeam, refetchTeamUsers, t]
  );

  const isUnchanged = useMemo(
    () =>
      teamName.trim() === (teamData?.name ?? '').trim() &&
      teamDescription.trim() === (teamData?.description ?? '').trim(),
    [teamName, teamDescription, teamData]
  );

  const isAnyLoading = isTeamLoading || isUsersLoading || isUsersFetching;

  const allUsers = users?.result ?? [];
  const teamUsers = teamUsersData?.result ?? [];

  const availableUsers = useMemo(
    () =>
      allUsers
        .filter(user => !teamUsers.some(teamUser => teamUser.id === user.id))
        .map(user => ({
          label: `${user.first_name} ${user.last_name}`,
          value: user.id
        })),
    [allUsers, teamUsers]
  );

  // Brand-aware semantic colors
  const dangerColor = isDark ? 'rgb(248,113,113)' : 'rgb(220,38,38)';
  const dangerHoverBg = isDark
    ? 'rgba(239,68,68,0.15)'
    : 'rgba(239,68,68,0.10)';

  return (
    <Modal
      isOpen={editTeam}
      onClose={handleClose}
      size="small"
      title={teamData?.name || t('edit-team')}
      caption={t('edit-team-name-and-description')}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="save"
          type="submit"
          form={FORM_ID}
          label={isSubmitting ? t('updating') : t('update')}
          size="small"
          variant="primary"
          disabled={isUnchanged || isSubmitting}
        />
      ]}
    >
      {isAnyLoading ? (
        <p
          className="w-full text-sm py-10 text-center"
          style={{ color: getColour('inactiveText') }}
        >
          {t('loading')}...
        </p>
      ) : (
        <form
          id={FORM_ID}
          className="flex flex-col gap-5 w-full px-2"
          onSubmit={e => {
            e.preventDefault();
            if (!isSubmitting && !isUnchanged) handleSaveChanges();
          }}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          {/* Name & Description */}
          <div className="grid gap-4">
            <Input
              id="name"
              label={t('name')}
              placeholder={t('enter-team-name')}
              type="text"
              autoComplete="off"
              value={teamName}
              onChange={e => setTeamName(e.target.value)}
              wrapperClass="w-full"
            />
            <Input
              id="description"
              label={t('description')}
              placeholder={t('enter-team-description')}
              type="text"
              autoComplete="off"
              value={teamDescription}
              onChange={e => setTeamDescription(e.target.value)}
              wrapperClass="w-full"
            />
          </div>

          {/* Team Members Section */}
          <fieldset className="w-full">
            <div className="flex items-center justify-between mb-3">
              <legend
                className="block text-sm font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('team-members')}
              </legend>

              {/* Stacked avatar preview */}
              {teamUsers.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {teamUsers.slice(0, MAX_PREVIEW_AVATARS).map(u => (
                      <div
                        key={u.id}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold leading-none"
                        style={{
                          backgroundColor: getBrandTint(0.15),
                          color: getColour('activeText'),
                          border: `2px solid ${getColour('secondLayer')}`
                        }}
                        title={`${u.first_name} ${u.last_name}`}
                        aria-hidden="true"
                      >
                        {getInitials(u.first_name, u.last_name)}
                      </div>
                    ))}
                    {teamUsers.length > MAX_PREVIEW_AVATARS && (
                      <div
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold leading-none"
                        style={{
                          backgroundColor: getColour('appSoft'),
                          color: getColour('inactiveText'),
                          border: `2px solid ${getColour('secondLayer')}`
                        }}
                        aria-hidden="true"
                      >
                        +{teamUsers.length - MAX_PREVIEW_AVATARS}
                      </div>
                    )}
                  </div>
                  <span
                    className="text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {teamUsers.length}
                  </span>
                </div>
              )}
            </div>

            {/* Add Member Select */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1">
                <SelectInput
                  id="add-member-select"
                  placeholder={t('search-and-select-user')}
                  options={availableUsers}
                  value={selectedUser}
                  onChange={(value: { label: string; value: number } | null) =>
                    setSelectedUser(value)
                  }
                  isClearable
                  isSearchable
                />
              </div>
              <Button
                type="button"
                label={t('add')}
                size="small"
                variant="primary"
                onClick={handleAddUser}
                disabled={!selectedUser}
              />
            </div>

            {/* Members List */}
            {teamUsers.length > 0 ? (
              <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                {teamUsers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-2xl"
                    style={{
                      backgroundColor: getColour('appSoft'),
                      border: `1px solid ${getColour('hairline')}`
                    }}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold leading-none"
                        style={{
                          backgroundColor: getBrandTint(0.15),
                          color: getColour('activeText'),
                          border: `1px solid ${getBrandTint(0.25)}`
                        }}
                        aria-hidden="true"
                      >
                        {getInitials(user.first_name, user.last_name)}
                      </div>
                      <span
                        className="text-sm font-medium truncate"
                        style={{ color: getColour('normalText') }}
                      >
                        {user.first_name} {user.last_name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user.id)}
                      className="rounded-full p-1 transition-colors"
                      style={{ color: dangerColor }}
                      onMouseEnter={e => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.backgroundColor = dangerHoverBg;
                      }}
                      onMouseLeave={e => {
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.backgroundColor = '';
                      }}
                      aria-label={t('remove-member')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center gap-2 py-6 rounded-2xl"
                style={{
                  backgroundColor: getColour('appSoft'),
                  border: `1px dashed ${getColour('hairline')}`
                }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ border: `2px dashed ${getColour('hairline')}` }}
                  aria-hidden="true"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeDasharray="3 2"
                      opacity="0.4"
                    />
                  </svg>
                </div>
                <p
                  className="text-sm"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('no-members-in-team')}
                </p>
              </div>
            )}
          </fieldset>
        </form>
      )}
    </Modal>
  );
};

export default TeamsUpdateForm;
